import { arrayBufferToBase64, base64ToArrayBuffer } from '../utils/utils';
import { UserKeyType } from '../vault/user-key-type';
import { Vault } from '../vault/vault';
import { VaultOperation } from '../vault/vault-operation';
import { getBytesToBeSigned } from '../vault/vault-utils';
import { MonotonicTimeSource } from './time-service';
export class CompanyKeyService {
    constructor(cryptoService, initializeService, uiFrameAdapter, vaultRepository, companyKeyRepository, logger) {
        this.cryptoService = cryptoService;
        this.initializeService = initializeService;
        this.uiFrameAdapter = uiFrameAdapter;
        this.vaultRepository = vaultRepository;
        this.companyKeyRepository = companyKeyRepository;
        this.logger = logger;
    }
    async createCompanyKey() {
        const timeService = new MonotonicTimeSource();
        try {
            this.logger.logInformation('Initialize company key: Generate new company key pair...');
            const addedAt = timeService.now();
            const lastSeenId = 0;
            const verifiedById = 0;
            const operation = VaultOperation.AddKey;
            const keyType = UserKeyType.Company;
            const version = 1;
            // generate private and public key pair
            const cryptoKeyPair = await this.cryptoService.generateKey();
            // export private key
            const privateKeyBytes = await this.cryptoService.exportPrivateKey(cryptoKeyPair.privateKey);
            // export public key
            const publicKeyBytes = await this.cryptoService.exportPublicKey(cryptoKeyPair.publicKey);
            const publicKeyByteArray = new Uint8Array(publicKeyBytes);
            // sign
            const requestorSignatureBase64Encoded = new Uint8Array(await this.cryptoService.sign(cryptoKeyPair.privateKey, getBytesToBeSigned(this.uiFrameAdapter.userId, addedAt, publicKeyByteArray, lastSeenId, verifiedById, operation, keyType, version)));
            // compose add-private-key request
            const companyKey = {
                userId: this.uiFrameAdapter.userId,
                addedAt,
                privateKey: arrayBufferToBase64(privateKeyBytes),
                publicKey: arrayBufferToBase64(publicKeyBytes),
            };
            // compose vault entry
            const vaultEntry = {
                id: 0,
                userId: this.uiFrameAdapter.userId,
                addedAt,
                verifiedById,
                lastSeenId,
                requestorPublicKey: publicKeyByteArray,
                requestorSignature: requestorSignatureBase64Encoded,
                verifierSignature: requestorSignatureBase64Encoded,
                blockchainSignature: requestorSignatureBase64Encoded,
                operation,
                keyType,
                version: 1
            };
            // store keys (only store private key if public key stored successfully)
            this.logger.logInformation('Initialize company key: Store root entry...');
            await this.companyKeyRepository.addCompanyPrivateKey(companyKey);
            await this.vaultRepository.addVaultEntry(vaultEntry);
            const newVault = await Vault.Create([vaultEntry]);
            await this.initializeService.initialize({ keyPair: cryptoKeyPair, signerEntry: vaultEntry, timeSource: timeService });
            return { vault: newVault, keyPair: cryptoKeyPair };
        }
        catch (ex) {
            this.logger.logError(`Initialize company key: ${ex}`);
            throw ex;
        }
    }
    async getCompanyKey() {
        const response = await this.companyKeyRepository.getCompanyPrivateKey();
        if (!response) {
            return null;
        }
        const [importedPrivateKey, importedPublicKey] = await Promise.all([
            this.cryptoService.importPrivateKey(base64ToArrayBuffer(response.privateKey)),
            this.cryptoService.importPublicKey(base64ToArrayBuffer(response.publicKey))
        ]);
        return { privateKey: importedPrivateKey, publicKey: importedPublicKey };
    }
    async isKeyCreationEnabled() {
        return await this.companyKeyRepository.isKeyCreationEnabled();
    }
}
